import { AitRowData } from "./AitRowData";
import { AitHeaderRowData } from "./interface";
import { newCell, newHeaderCell } from "./newCell";

/**
 * Create a new body row populated with default cells.
 *
 * Each new cell is created by `newCell` and initialised with the provided
 * `defaultCellWidth`, `blank` payload and the corresponding `columnId` from
 * `columnIds`. The function generates unique `aitid` values for the row and
 * for each cell.
 *
 * @template T - The cell payload type
 * @param defaultCellWidth - Width to use for newly created cells
 * @param blank - Default payload value used to initialise each new cell
 * @param l - Number of cells to create (defaults to 1 when falsy)
 * @param columnIds - Array of columnId strings to assign to each new cell; may be shorter than `l`
 * @returns A new `AitRowData<T>` containing the created cells
 */
export const newRow = <T>(
  defaultCellWidth: number,
  blank: T,
  l: number,
  columnIds: string[],
): AitRowData<T> => {
  const newRow: AitRowData<T> = {
    aitid: crypto.randomUUID(),
    cells: [],
    coreTrafficLight: "BlankNotEvaluated",
  };
  // Add new cells in a loop to avoid duplicate aitid
  for (let i = 0; i < l; i++) {
    const columnId = columnIds[i];
    newRow.cells.push(newCell(defaultCellWidth, blank, columnId));
  }
  return newRow;
};

/**
 * Create a new header row populated with default header cells.
 *
 * Behaviour mirrors `newRow` but uses `newHeaderCell` to create header-style
 * cells. Each created header cell receives its `columnId` (when provided),
 * and the returned row is initialised with a fresh `aitid`.
 *
 * @template T - The header cell payload type
 * @param defaultCellWidth - Width to use for newly created header cells
 * @param blank - Default payload value used to initialise each new header cell
 * @param l - Number of header cells to create (defaults to 1 when falsy)
 * @param columnIds - Array of columnId strings to assign to each new header cell
 * @returns A new `AitHeaderRowData<T>` containing the created header cells
 */
export const newHeaderRow = <T>(
  defaultCellWidth: number,
  blank: T,
  l: number,
  columnIds: string[],
): AitHeaderRowData<T> => {
  const newRow: AitHeaderRowData<T> = {
    aitid: crypto.randomUUID(),
    cells: [],
    coreTrafficLight: "BlankNotEvaluated",
  };
  // Add new cells in a loop to avoid duplicate aitid
  for (let i = 0; i < l; i++) {
    const columnId = columnIds[i];
    newRow.cells.push(newHeaderCell(defaultCellWidth, blank, columnId));
  }
  return newRow;
};
